/**
 * src/pages/reports/_reportProgress.js — what the CEO report shows while it is
 * being written. Card P219, from Daniel (1 Nov).
 *
 * ⛔⛔ THE DEFECT WAS SILENCE. Clicking "Generate report" produced no sign that
 * anything was happening, and the call takes 30–60 seconds (MEASURED LIVE:
 * ~50 s). Carlos concluded it was broken and clicked again. Worse, the
 * PREVIOUS brief stayed on screen throughout, so the one thing he could see
 * was a report that looked current and was not.
 *
 * ⚠⚠ EVERY MESSAGE HERE IS CLIENT-SIDE AND TIME-BASED, AND THAT IS STATED ON
 * SCREEN RATHER THAN IMPLIED. The server does one request and one response;
 * it reports no phases, so nothing here can know what it is actually doing.
 * The steps below are an honest approximation of the sequence and are
 * labelled as approximate. ⛔ NO PERCENTAGES AND NO PROGRESS BAR: both claim a
 * measured fraction of a job whose length we cannot see, and a bar that sits
 * at 80% for thirty seconds teaches the reader to distrust the next one.
 *
 * ⛔ THE RETRY IS NOT OBSERVABLE FROM HERE EITHER. The server's one retry on
 * stop_reason=max_tokens (P209) happens INSIDE the single call, so the client
 * learns of it only in the response. The late message uses Daniel's wording —
 * "Taking a little longer — refining the report…" — on a TIMER, and the panel
 * says the steps are approximate, so it is not a claim that a retry began. If
 * the server ever streams phases, phaseAt() is the one place to replace.
 */

import React from 'react';
import { C, S, FONT } from './_overviewTheme';

/**
 * The step schedule. Boundaries come from the measured ~50 s run, placed so
 * the text changes often enough to read as alive without implying precision.
 */
export const PHASES = Object.freeze([
  { at: 0,      text: 'Gathering the dashboard figures…' },
  { at: 12000,  text: 'Writing the brief…' },
  { at: 35000,  text: 'Formatting charts…' },
  /* ⚠ Daniel's wording for the retry path. Time-based — see the header. */
  { at: 60000,  text: 'Taking a little longer — refining the report…' },
  { at: 90000,  text: 'Still working…' },
]);

/**
 * ⛔ The client stops waiting at 150 s. The server's own ceiling is longer
 * (one 60 s timeout per attempt, up to three attempts inside
 * callClaudeWithRetry, and P209 may make a second call), so this is the
 * EARLIER of the two by design: a reader who has watched a spinner for two
 * and a half minutes needs a button, not more patience.
 */
export const GIVE_UP_MS = 150000;

/** The step for an elapsed time. Last boundary at or below `ms` wins. */
export function phaseAt(ms) {
  const t = Number(ms) || 0;
  let hit = PHASES[0];
  for (const p of PHASES) if (t >= p.at) hit = p;
  return hit.text;
}

/** "0:23" — m:ss, minutes uncapped. */
export function formatElapsed(ms) {
  const total = Math.max(0, Math.floor((Number(ms) || 0) / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * ⚠ GUARDED, NOT ASSUMED. jsdom has no matchMedia unless a test installs one,
 * and an unguarded call throws during render — which would turn a motion
 * preference into a blank dialog. Anything unknown is treated as "motion is
 * fine", because that is the behaviour every existing surface already has.
 */
export function prefersReducedMotion() {
  try {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return Boolean(window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  } catch (_) {
    return false;
  }
}

/**
 * The spinner. ⚠ Under reduced motion this is a STATIC glyph, not a slowed
 * animation — the point is to remove the movement, and the element still
 * carries meaning because the text beside it says what is happening.
 */
export function Spinner({ reduced, size = 14 }) {
  if (reduced) {
    return (
      <span data-testid="report-spinner-static" aria-hidden="true"
            style={{ display: 'inline-block', width: size, height: size, lineHeight: `${size}px`,
                     textAlign: 'center', color: C.primary, fontSize: size }}>
        ◴
      </span>
    );
  }
  /*
   * ⛔⛔ Card P232 item 2 — THE TRACK IS UNCHANGED, AND THE REASON IS A
   * MEASUREMENT THAT CONTRADICTED THE ASK.
   *
   * The card asked for a darker track so the arc would read at 14–16px, with
   * at least 3:1 between them. Measured (WCAG relative luminance):
   *
   *     arc C.primary vs track C.border   5.40:1   <- what already shipped
   *     arc C.primary vs track C.rule     4.96:1   <- the proposed "darker"
   *
   * C.rule is darker than C.border, so moving to it moves the track TOWARD
   * the arc and LOWERS the contrast. The existing pairing already cleared the
   * 3:1 bar by a wide margin, so the still circle Daniel saw was never a
   * contrast problem — it was the missing animation, nothing else. Changing
   * the colour would have been a cosmetic change sold as a fix, and a
   * measurably worse one.
   *
   * ⚠ WHAT DID CHANGE IS THE ARC WEIGHT. A 2px arc on a 16px circle is a
   * thin thread; scaling the ring with the size gives the rotation something
   * to show without touching the colours.
   */
  const ring = Math.max(2, Math.round(size / 7));
  return (
    <span data-testid="report-spinner" aria-hidden="true" className="ceo-report-spinner"
          style={{ display: 'inline-block', width: size, height: size,
                   border: `${ring}px solid ${C.border}`, borderTopColor: C.primary,
                   borderRadius: '50%' }} />
  );
}

/**
 * The working panel — what replaces the report area while a brief is being
 * written.
 *
 * ⛔⛔ IT REPLACES THE PREVIOUS REPORT, IMMEDIATELY. That is half the card: a
 * stale brief left on screen under a spinner is worse than an empty panel,
 * because it is the thing the reader is looking at and it looks current.
 */
export function WorkingPanel({ elapsedMs = 0, pipelineLabel, rangeLabel, reduced = false }) {
  return (
    <section
      data-testid="ai-report-working"
      className="ceo-ai-report-working"
      /*
       * ⚠ aria-busy on the REGION, role=status + polite on the message. An
       * assertive live region would interrupt a screen-reader user every time
       * the step text changed, five times in a minute, to tell them something
       * they already know is happening.
       */
      aria-busy="true"
      style={{ ...S.card, display: 'flex', flexDirection: 'column', gap: 8, fontFamily: FONT }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Spinner reduced={reduced} size={16} />
        <strong style={{ fontSize: 14, color: C.text }}>
          Generating your report
        </strong>
        <span data-testid="ai-report-elapsed"
              style={{ marginLeft: 'auto', fontSize: 13, color: C.muted,
                       fontVariantNumeric: 'tabular-nums' }}>
          {formatElapsed(elapsedMs)}
        </span>
      </div>

      <div role="status" aria-live="polite" data-testid="ai-report-working-status"
           style={{ fontSize: 13, color: C.body, lineHeight: 1.55 }}>
        {phaseAt(elapsedMs)}
      </div>

      <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
        This usually takes 30–60 seconds.
      </div>

      {/* ⭐ WHAT is being generated. Carlos opens this from a dashboard that
          has a pipeline picker and a period picker; seeing them echoed back
          is how he knows the wait belongs to the question he asked. */}
      {(pipelineLabel || rangeLabel) ? (
        <div data-testid="ai-report-working-scope"
             style={{ fontSize: 12, color: C.faint, lineHeight: 1.5 }}>
          {[pipelineLabel, rangeLabel].filter(Boolean).join(' · ')}
        </div>
      ) : null}

      {/* ⛔ THE HONESTY LINE. The steps above are a client-side timer, not a
          report of server state, and the panel says so rather than letting
          the wording imply otherwise. */}
      <div style={{ fontSize: 11, color: C.faint, lineHeight: 1.5 }}>
        Steps are approximate — the report is written in one pass and does not
        report its progress.
      </div>
    </section>
  );
}

export default WorkingPanel;
